<template>
  <ui-dialog
    :show="isOpen"
    scrollable
    persistent
    max-width="833"
    custom-class="facilities-details-url-attributes-dialog"
    @input="close"
  >
    <template #header>
      <h3 class="url-attributes-dialog__heading">
        {{ $t(title) }}
      </h3>
      <p class="url-attributes-dialog__description">
        {{ $t(description) }}
      </p>
    </template>
    <template #default>
      <template v-if="!deleteMode">
        <v-list
          v-if="loaded && urlAttributes && urlAttributes.length"
          class="url-attributes-dialog__list min-h"
        >
          <v-list-item
            class="url-attributes-dialog__list-item"
            v-for="(attribute, attributeIndex) in urlAttributes"
            :key="attribute.gmb_attribute.id"
          >
            <p class="attribute__display-name">
              {{ attribute.gmb_attribute.display_name }}
            </p>
            <v-list
              v-if="attribute.values && attribute.values.length"
              class="attribute__fields-list"
            >
              <v-list-item
                v-for="(value, valueIndex) in attribute.values"
                :key="value.id"
                class="attribute__fields-list-item"
              >
                <v-text-field
                  :ref="value.id"
                  v-model.trim="
                    urlAttributes[attributeIndex].values[valueIndex].url
                  "
                  v-validate="{ url: {} }"
                  :data-vv-as="$t('message.common.urlAttribute')"
                  :name="value.id"
                  :error-messages="errors.collect(value.id)"
                  clearable
                  :counter="maxCharactersCount"
                  :maxlength="maxCharactersCount"
                  :disabled="isAttributeDisabled(attribute)"
                  >{{ value.url }}</v-text-field
                >
                <v-btn
                  v-if="
                    attribute.gmb_attribute.is_repeatable &&
                      !isAttributeDisabled(attribute)
                  "
                  icon
                  color="pink"
                  @click="deleteUrlAttribute(attributeIndex, valueIndex)"
                  class="ml-3 mb-2"
                >
                  <v-icon>
                    icon-trash
                  </v-icon>
                </v-btn>
              </v-list-item>
            </v-list>
            <div v-else class="attribute__no-attributes">
              <p>
                {{
                  $t(
                    "message.facilityDetails.urlAttributes.dialog.noAttributes"
                  )
                }}
              </p>
            </div>
            <template v-if="!isAttributeDisabled(attribute)">
              <ui-button
                v-if="
                  attribute.gmb_attribute.is_repeatable &&
                    attribute.values &&
                    attribute.values.length < maxAttributesCount
                "
                icon="mdi-plus"
                @click="addUrlAttribute(attributeIndex)"
              >
                {{
                  $t(
                    "message.facilityDetails.urlAttributes.dialog.addAttributeBtn"
                  )
                }}
              </ui-button>
            </template>
            <template v-else>
              <InfoBox
                :text="
                  $t('message.facilityDetails.urlAttributes.dialog.notEditable')
                "
              />
            </template>
          </v-list-item>
        </v-list>
        <div v-else-if="loaded && urlAttributes && urlAttributes.length === 0">
          <InfoBox
            class="mt-2"
            :text="
              $t(
                `message.facilityDetails.urlAttributes.dialog.${
                  facilitiesIds.length === 1
                    ? 'noAttributesAvailable'
                    : 'noAttributesAvailableMassEdit'
                }`
              )
            "
          />
        </div>
        <div v-else class="min-h">
          <SectionLoader :status="loading" :opacity="1" />
        </div>
      </template>
      <template v-else>
        <v-list
          v-if="loaded && urlAttributes && urlAttributes.length"
          class="url-attributes-dialog__delete-list"
        >
          <v-virtual-scroll
            :items="urlAttributes"
            :item-height="listItemHeight"
            :height="deleteListHeight"
          >
            <template v-slot:default="{ item }">
              <v-list-item
                :key="item.id"
                :ripple="false"
                @click="toggleDeleteAttribute(item)"
                class="url-attributes-dialog__delete-list-item"
                :class="{ selected: item.isSelectedToDelete }"
              >
                <v-list-item-content class="d-flex align-center">
                  <v-icon
                    size="21"
                    :color="item.isSelectedToDelete ? 'pink' : 'text'"
                    class="mr-3"
                  >
                    {{ item.isSelectedToDelete ? "icon-trash" : "icon-plus" }}
                  </v-icon>
                  <p>
                    {{ item.gmb_attribute.display_name }}
                  </p>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-list>
        <div v-else-if="loaded && urlAttributes && urlAttributes.length === 0">
          <InfoBox
            v-if="deleteMode"
            class="mt-2"
            :text="
              $t(
                'message.facilityDetails.urlAttributes.dialog.noAttributesToDeleteMassEdit'
              )
            "
          />
          <InfoBox
            v-else
            class="mt-2"
            :text="
              $t(
                'message.facilityDetails.urlAttributes.dialog.noAttributesAvailableMassEdit'
              )
            "
          />
        </div>
        <div v-else class="min-h">
          <SectionLoader :status="loading" :opacity="1" />
        </div>
      </template>
    </template>
    <template #actions>
      <div class="url-attributes-dialog__actions">
        <ui-button outlined @click="close">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button color="primary" :disabled="loading" @click="submit">
          {{ $t(`message.facilityDetails.urlAttributes.dialog.applyBtn`) }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import cuid from "cuid";
import serviceHandler from "@/helpers/handlers/service";
import gmbAttributesService from "@/services/gmbAttributes";
import { uniqBy } from "lodash";

import InfoBox from "@/components/ui/InfoBox.vue";
import SectionLoader from "@/components/Loader/SectionLoader";

import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";

export default {
  name: "UrlAttributesDialog",
  components: {
    InfoBox,
    SectionLoader
  },
  props: {
    facilitiesIds: {
      type: Array,
      required: true
    },
    initialUrlAttributes: {
      type: Array,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: "message.facilityDetails.urlAttributes.dialog.title"
    },
    description: {
      type: String,
      default: "message.facilityDetails.urlAttributes.dialog.description"
    },
    deleteMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loaded: false,
      loading: false,
      urlAttributes: [],
      maxAttributesCount: 10,
      maxCharactersCount: 2048,
      listItemHeight: 50
    };
  },

  computed: {
    deleteListHeight() {
      return this.urlAttributes && this.urlAttributes.length < 8
        ? this.listItemHeight * (this.urlAttributes.length + 1)
        : this.listItemHeight * 8;
    }
  },

  watch: {
    async isOpen(newValue) {
      if (newValue) {
        await this.fetchUrlAttributes();
      } else {
        this.urlAttributes = [];

        setTimeout(() => {
          this.loaded = false;
        }, 150);
      }
    }
  },

  methods: {
    async fetchUrlAttributes() {
      this.loading = true;

      this.urlAttributes = await serviceHandler(
        gmbAttributesService.fetchAvailableGmbAttributes({
          facilitiesIds: this.facilitiesIds,
          onlySet: this.deleteMode,
          valueTypes: [GmbAttributeValueType.URL]
        })
      );

      this.mapAvailableAttributes();

      if (this.deleteMode) {
        this.urlAttributes = this.urlAttributes.filter(
          attribute => !this.isAttributeDisabled(attribute)
        );
      }

      this.fillInitialAttributes();

      this.loaded = true;
      this.loading = false;
    },

    mapAvailableAttributes() {
      this.urlAttributes = this.urlAttributes.map(attribute => {
        const mappedAttr = { gmb_attribute: { ...attribute }, values: [] };

        if (this.deleteMode) {
          mappedAttr.isSelectedToDelete = false;
        }
        return mappedAttr;
      });
    },

    fillInitialAttributes() {
      this.initialUrlAttributes.forEach(attribute => {
        const indexFound = this.urlAttributes.findIndex(
          avAttr => avAttr.gmb_attribute.id === attribute.gmb_attribute.id
        );

        if (indexFound >= 0 && attribute.values) {
          if (this.deleteMode) {
            this.urlAttributes[indexFound].isSelectedToDelete = true;
          } else {
            attribute.values.forEach(value => {
              this.addUrlAttribute(indexFound, value.url);
            });
          }
        } else {
          if (this.isAttributeDisabled(attribute)) {
            // readonly attribute, need to be added to urlAttributes
            attribute.values = attribute.values.map(value => {
              return { ...value, id: cuid() };
            });
            this.urlAttributes.push(attribute);
          }
        }
      });

      this.urlAttributes.forEach((attribute, attributeIndex) => {
        if (attribute.values && attribute.values.length === 0) {
          this.addUrlAttribute(attributeIndex);
        }
      });
    },

    addUrlAttribute(index, url = "") {
      const id = cuid();
      this.urlAttributes[index].values.push({ url, id });

      if (!this.deleteMode) {
        setTimeout(() => {
          this.$refs[id][0].focus();
        }, 50);
      }
    },

    deleteUrlAttribute(attributeIndex, valueIndex) {
      if (attributeIndex >= 0 && valueIndex >= 0) {
        this.urlAttributes[attributeIndex].values.splice(valueIndex, 1);
      }
    },

    isAttributeDisabled(attribute) {
      return attribute.gmb_attribute.attribute_id === "url_order_ahead";
    },

    toggleDeleteAttribute(attribute) {
      const labelIndex = this.urlAttributes.findIndex(
        ({ gmb_attribute }) => gmb_attribute.id === attribute.gmb_attribute.id
      );

      if (labelIndex >= 0) {
        this.urlAttributes[labelIndex].isSelectedToDelete = !this.urlAttributes[
          labelIndex
        ].isSelectedToDelete;
      }
    },

    async submit() {
      const isValid = await this.$validator.validate();
      if (isValid) {
        this.save();
      } else {
        this.$notify({
          group: "default",
          type: "error",
          text: this.$t(
            "message.facilityDetails.urlAttributes.dialog.validationError"
          )
        });
      }
    },

    close() {
      this.$emit("close");
    },

    save() {
      if (this.deleteMode) {
        this.urlAttributes = this.urlAttributes.filter(
          ({ isSelectedToDelete }) => isSelectedToDelete
        );
        this.urlAttributes.forEach(attribute => {
          delete attribute.isSelectedToDelete;
          attribute.values = [];
        });
      } else {
        this.urlAttributes.forEach(attribute => {
          // delete id property (not needed for patch request)
          attribute.values.forEach(value => {
            delete value.id;
          });

          // filter out values with empty url
          attribute.values = uniqBy(
            [...attribute.values.filter(value => !!value.url)],
            "url"
          );
        });

        // filter out attributes with no values provided
        this.urlAttributes = this.urlAttributes.filter(
          attr => !!attr.values.length
        );
      }
      this.$emit("save", [...this.urlAttributes]);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.url-attributes-dialog {
  color: $text;

  &__heading {
    line-height: 36px;
  }

  &__description {
    @include font-size(14px);
    color: $text;
    opacity: 0.4;
    line-height: 20px;
  }

  &__list {
    padding: 0;
  }

  &__list-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    min-height: unset;

    &:not(:first-child) {
      margin-top: $spacing * 2;
    }
  }

  &__actions {
    margin-top: $spacing * 2;
    width: 100%;
    display: flex;
    justify-content: center;

    & > button:first-child {
      margin-right: $spacing;
    }

    & > button:last-child {
      margin-left: $spacing;
      min-width: 230px !important;
    }
  }

  &__delete-list {
    .v-list-item {
      &__content {
        * {
          flex: unset;
        }
      }
    }
  }

  &__delete-list-item {
    height: 46px;
    border: 1px solid $text;
    border-radius: 30px;
    overflow: hidden;
    @include font-size(14px);
    border: 1px solid rgba($text, 0.14);
    transition: all 0.15s ease-in;
    margin-right: $spacing;

    p {
      margin-bottom: 0;
    }

    &.selected {
      border-color: $pink;
      background-color: $pink-light;
    }
  }
}

.attribute {
  &__display-name {
    width: 100%;
    @include font-size(14px);
    font-weight: 700;
    line-height: 20px;
    padding: $spacing 0;
    margin-bottom: 0;
    border-bottom: 1px solid $light-3;
  }

  &__fields-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  &__fields-list-item {
    display: flex;
    flex-direction: row;
    padding: 0;
    width: 100%;
  }

  &__no-attributes {
    width: 100%;

    p {
      margin-top: $spacing * 2;
    }
  }
}

.min-h {
  min-height: 300px;
}
</style>

<style lang="scss">
.facilities-details-url-attributes-dialog {
  position: relative;

  .v-card__title {
    padding-bottom: 0 !important;
  }
  .v-card__text {
    padding-top: 0 !important;
  }
}
</style>
